import {
  createAbortError,
  isProviderSummary,
  notifyDetailFailure,
  playableResource,
} from '@src/web-stories-wp/story-editor/src/app/media/media3p/api/mediaDetailsState';
import useMedia3pApi from '@src/web-stories-wp/story-editor/src/app/media/media3p/api/useMedia3pApi';
import { useCallback, useEffect, useRef } from '@web-stories-wp/react';
import { v4 as uuidv4 } from 'uuid';
import { resourceList } from '@web-stories-wp/media';

import useLibrary from '@src/web-stories-wp/story-editor/src/components/library/useLibrary';
import { useAPI } from '@src/web-stories-wp/story-editor/src/app/api';
import { useStory } from '@src/web-stories-wp/story-editor/src/app';
import { useSnackbar } from '@web-stories-wp/design-system';
import { preloadImage, preloadVideoMetadata } from '@web-stories-wp/media';

function useInsertLibraryMediaElement() {
  const { insertElement } = useLibrary((state) => ({
    insertElement: state.actions.insertElement,
  }));

  const { updateElementsByResourceId, deleteElementsByResourceId } = useStory(
    ({
      actions: { updateElementsByResourceId, deleteElementsByResourceId },
    }) => ({
      updateElementsByResourceId,
      deleteElementsByResourceId,
    })
  );

  const {
    actions: { download3pAsset },
  } = useAPI();

  const { showSnackbar } = useSnackbar();
  const {
    actions: { details },
  } = useMedia3pApi();
  const lifecycle = useRef(null);
  const pendingInsertions = useRef(new Map());
  useEffect(() => {
    const controller = new AbortController();
    const pending = pendingInsertions.current;
    lifecycle.current = controller;
    return () => {
      controller.abort();
      pending.clear();
    };
  }, [details]);
  const reportDetailFailure = useCallback(
    (error) => notifyDetailFailure(showSnackbar, error),
    [showSnackbar]
  );
  // Every ScorePlay insertion path (click, canvas drop, drop target) resolves
  // details, recovers missing dimensions and confirms the context is current.
  const resolvePlayableVideo = useCallback(
    async (resource) => {
      const controller = lifecycle.current;
      if (!details || !controller || controller.signal.aborted)
        throw new Error('Video unavailable');
      let ready = await details.resolve(resource, {
        signal: controller.signal,
      });
      if (ready.galleryOnly || !/^https?:\/\//i.test(ready.src || ''))
        throw new Error('Video unavailable');
      if (!(ready.width > 0 && ready.height > 0)) {
        const media = await preloadVideoMetadata(ready.src);
        if (!(media.videoWidth > 0 && media.videoHeight > 0))
          throw new Error('Video dimensions unavailable');
        ready = {
          ...ready,
          width: media.videoWidth,
          height: media.videoHeight,
        };
      }
      // Metadata I/O can outlive the editor or library that resolved the video.
      if (
        controller.signal.aborted ||
        lifecycle.current !== controller ||
        !details.isCurrent(ready)
      )
        throw createAbortError();
      return playableResource(ready);
    },
    [details]
  );
  const insertProviderVideo = useCallback(
    (resource, element = {}) => {
      const key = `${resource.mediaProviderId}:${resource.id}`;
      if (pendingInsertions.current.has(key))
        return pendingInsertions.current.get(key);
      const { baseColor } = resource;
      const promise = resolvePlayableVideo(resource)
        .then((ready) =>
          insertElement(ready.type, {
            ...element,
            ...(resource.galleryOnly && element.width > 0
              ? { height: (element.width * ready.height) / ready.width }
              : {}),
            // Keep the tile's sampled colour, as other providers do.
            resource: baseColor ? { ...ready, baseColor } : ready,
          })
        )
        .catch(reportDetailFailure);
      pendingInsertions.current.set(key, promise);
      const forget = () => {
        if (pendingInsertions.current.get(key) === promise)
          pendingInsertions.current.delete(key);
      };
      promise.then(forget, forget);
      return promise;
    },
    [insertElement, reportDetailFailure, resolvePlayableVideo]
  );

  // A placement marks its loading resource with a `pendingPlacementId`.
  // Completion and failure touch only elements still carrying that mark: the
  // element the placement inserted, wherever it now is, and any copy made while
  // it was loading. Earlier placements of the same item never carry it, so they
  // are never updated or removed. A drop onto a target inserts nothing until the
  // download succeeds, so its resource has no mark and nothing is touched here.
  const startDownloadingResource = useCallback(
    async (resource) => {
      if (isProviderSummary(resource))
        return resolvePlayableVideo(resource).catch(reportDetailFailure);
      const { id, pendingPlacementId } = resource;
      const isPending = Boolean(pendingPlacementId);

      try {
        const shouldDownload = resource.sizes.full?.requestFullUrl;

        if (!shouldDownload) {
          return resource;
        }

        const assetId = resource.sizes.full.source_url;

        // Download full asset
        const { url } = await download3pAsset(resource.mediaProviderId, {
          assetId,
        });

        // Create new resource object (don't mutate frozen original)
        const updatedResource = {
          ...resource,
          src: url,
          sizes: {
            ...resource.sizes,
            full: {
              ...resource.sizes.full,
              requestFullUrl: false,
              source_url: url,
            },
          },
        };

        // Update element with downloaded URL
        try {
          if (isPending) {
            updateElementsByResourceId({
              id,
              pendingPlacementId,
              properties: () => {
                return {
                  resource: updatedResource,
                };
              },
            });
          }
        } catch (updateError) {
          // The update skips elements removed during the download; cache the
          // copy for next time if the update itself fails.
          resourceList.set(id, {
            type: 'fullsize',
            url,
          });
          return Promise.resolve();
        }

        // Preload media metadata and recover dimensions when the provider did
        // not expose them before Storyteller materialised the durable source.
        const media =
          resource.type === 'video'
            ? await preloadVideoMetadata(url)
            : await preloadImage(url, '');
        const discoveredWidth =
          resource.type === 'video' ? media.videoWidth : media.naturalWidth;
        const discoveredHeight =
          resource.type === 'video' ? media.videoHeight : media.naturalHeight;
        const discoveredDimensions =
          discoveredWidth > 0 &&
          discoveredHeight > 0 &&
          !(resource.width > 0 && resource.height > 0)
            ? { width: discoveredWidth, height: discoveredHeight }
            : {};

        // Cache the full URL
        resourceList.set(id, {
          type: 'fullsize',
          url,
        });

        // Update element - clear loading state, sizes and the pending mark
        const { pendingPlacementId: _settled, ...settledResource } =
          updatedResource;
        const finalResource = {
          ...settledResource,
          ...discoveredDimensions,
          loading: false,
          sizes: {},
        };

        try {
          if (isPending) {
            updateElementsByResourceId({
              id,
              pendingPlacementId,
              properties: () => {
                return {
                  resource: finalResource,
                };
              },
            });
          }
        } catch (updateError) {
          // Already cached; a removed element is simply skipped.
        }

        return finalResource;
      } catch (ex) {
        if (isPending) {
          deleteElementsByResourceId({ id, pendingPlacementId });
        }
        showSnackbar({
          message: 'Failed to download the asset. Try again.',
          dismissable: true,
        });
        return null;
      }
    },
    [
      deleteElementsByResourceId,
      download3pAsset,
      reportDetailFailure,
      resolvePlayableVideo,
      showSnackbar,
      updateElementsByResourceId,
    ]
  );

  const insertDownloadableAsset = useCallback(
    async (element) => {
      const { resource } = element;

      // Create new resource with loading state (don't mutate frozen original)
      const isValidUrl =
        resource.src?.startsWith('http://') ||
        resource.src?.startsWith('https://');
      const loadingResource = {
        ...resource,
        src: isValidUrl ? resource.src : '',
        loading: true,
        pendingPlacementId: uuidv4(),
      };

      insertElement(resource.type, { ...element, resource: loadingResource });
      return startDownloadingResource(loadingResource);
    },
    [insertElement, startDownloadingResource]
  );

  const insertResource = useCallback(
    (resource, thumbnailURL) => {
      if (isProviderSummary(resource)) return insertProviderVideo(resource);
      resourceList.set(resource.id, {
        url: thumbnailURL,
        type: 'cached',
      });

      if (
        resource.sizes.full?.requestFullUrl &&
        !!resource.sizes.full?.source_url
      ) {
        return insertDownloadableAsset({ resource });
      } else {
        // No download needed
        return insertElement(resource.type, { resource });
      }
    },
    [insertElement, insertDownloadableAsset, insertProviderVideo]
  );

  const insertImage = useCallback(
    (element) => {
      const { resource } = element;

      if (
        resource.sizes.full?.requestFullUrl &&
        !!resource.sizes.full?.source_url
      ) {
        insertDownloadableAsset(element);
      } else {
        insertElement(resource.type, element);
      }
    },
    [insertElement, insertDownloadableAsset]
  );

  const insertMediaElement = useCallback(
    (element) => {
      const { resource } = element;
      if (isProviderSummary(resource))
        return insertProviderVideo(resource, element);
      const type = element.type ?? resource?.type;
      if (type === 'image') {
        return insertImage(element);
      }

      if (
        resource?.sizes.full?.requestFullUrl &&
        !!resource.sizes.full?.source_url
      ) {
        return insertDownloadableAsset(element);
      }

      return insertElement(type, element);
    },
    [insertDownloadableAsset, insertElement, insertImage, insertProviderVideo]
  );

  return { insertResource, insertMediaElement, startDownloadingResource };
}

export default useInsertLibraryMediaElement;
